import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Avatar } from '../chat/Avatar'
import { SidebarSection } from '../sidebar/SidebarSection'
import { getSharedArtifacts, type SharedArtifact } from '../transport/relay'
import { openInArtifactWindow } from './artifactWindow'
import { sharedFrameHere } from './contentHost'
import { sharerOf } from './everywhere'

/**
 * SHARED WITH ME — owner, 2026-10-05: 「在 More -> Artifacts 的頁面, Drop down list:
 * Shared with me … 顯示其他使用者分享的 Artifact 頁面, 每個被分享的 Artifact 上面需要有
 * 頁面的標題以及是哪個使用者分享的相關的資訊」.
 *
 * One more group under Artifacts, beside the per-machine ones: what this account's
 * friends are sharing with it right now. Each row is the page's title and the friend
 * who shares it.
 *
 * ⚠️ A FRIEND'S PAGE IS NEVER FRAMED AT THE APP'S OWN ORIGIN, AND THAT MUST STAY TRUE.
 * There the framed page can reach `window.parent` — harmless for an artifact from this
 * account's own machine, a session takeover for anyone else's (everywhere.ts
 * `inAppViewGate`, docs/ARTIFACTS.md §3d). So a row here is never handed to
 * `onOpenArtifact`, the own-artifact door.
 *
 * WHERE A ROW OPENS — three steps, each tried only when the one before did not take the
 * tap:
 *
 *  1. THE APP'S OWN VIEWER, in the main pane — owner, 2026-10-05, ruling that artifacts
 *     move to a hostname of their own: a friend's shared artifact opens with the built-in
 *     viewer, not in a separate window. Only where the cloud serves artifacts from that
 *     other host and the address on this row passes `sharedViewGate` (contentHost.ts asks
 *     it): on another origin the page cannot reach into the App.
 *  2. THE SHELL'S ISOLATED WINDOW — owner, 2026-10-05, after meeting the pop-out in the
 *     Mac App: 「iOS / Mac App 需要如同使用自己的 App 一般, 可以直接使用 Avengents App 內建的
 *     web browser 來打開」 (artifactWindow.ts). It needs a ticket on the App's own origin,
 *     so it is the path of a cloud whose artifacts have NOT moved.
 *  3. THE LINK ITSELF. The row is, and stays, an ordinary link to the share address — a
 *     browser, a store build that has neither of the above, a click meant for a new tab.
 *     Outside the App the person signs in and sees what their account may see.
 *
 * ONLY WHAT CAN BE OPENED NOW. An artifact is served from its owner's machine; the list
 * comes from the relay's live registry, so one whose machine is off is not here — the
 * same moment its address would answer "that machine is offline".
 */
const SHARED_SECTION_ID = 'more-art:shared'

export function SharedWithMe({
  isOpen,
  onToggle,
  onOpenShared,
}: {
  isOpen: (id: string) => boolean
  onToggle: (id: string) => void
  /** The in-app viewer's door for a friend's artifact — step 1 above, and only that step. */
  onOpenShared: (a: SharedArtifact) => void
}) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['artifacts-shared'],
    queryFn: getSharedArtifacts,
    staleTime: 30_000,
    retry: false,
  })
  const rows = q.data?.artifacts ?? []
  return (
    <SidebarSection
      id={SHARED_SECTION_ID}
      label="Shared with me"
      count={q.data ? rows.length : undefined}
      isOpen={isOpen(SHARED_SECTION_ID)}
      onToggle={onToggle}
    >
      {q.isPending && <p className="more-note">Checking…</p>}
      {/* "Could not ask" and "nothing shared" are different sentences — the same rule
          the machine groups above follow for a machine that did not answer. */}
      {q.isError && <p className="more-note more-quiet">Could not check what is shared with you.</p>}
      {q.data && rows.length === 0 && <p className="more-note">Nothing is shared with you right now.</p>}
      {rows.map((a) => {
        const who = sharerOf(a)
        return (
          <a
            key={a.hash}
            className="sb-row art-row art-shared"
            href={a.url}
            target="_blank"
            rel="noreferrer noopener"
            title={`${a.title} — shared by ${who}`}
            onClick={(e) => {
              /*
               * A click that asks for somewhere else — a new tab, a new window, a
               * download — is the person choosing the browser, and the viewer does not
               * take it from them: that is the door the owner asked to keep open.
               */
              const plain = !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
              if (plain && sharedFrameHere(qc, a).ok) {
                e.preventDefault()
                onOpenShared(a)
                return
              }
              // The shell's isolated window took it: the link must not ALSO leave the App.
              if (openInArtifactWindow({ openUrl: a.open_url, title: a.title, by: `Shared by ${who}` })) e.preventDefault()
            }}
          >
            <Avatar picture={a.shared_by.picture} label={who} email={a.shared_by.email} className="art-avatar" />
            <span className="art-row-main">
              <span className="art-row-name">{a.title}</span>
              <span className="art-row-sub">Shared by {who}</span>
            </span>
          </a>
        )
      })}
    </SidebarSection>
  )
}
